/** Protocol versioning. Every envelope carries `v`; `system.hello` negotiates the highest common version. */

/** Current wire protocol version. */
export const PROTOCOL_VERSION = 1 as const;

/** Versions this build can speak (client and server share the list). */
export const SUPPORTED_PROTOCOL_VERSIONS: readonly number[] = [1];

/**
 * Version of this build (package.json "version"; tests/backend/protocol-hello.test.ts checks that they agree). The
 * backend reports its configured version (BackendConfig.api.serverVersion: the self-hosted server sets package.json's
 * version, the in-process backend defaults to this) in `system.hello`, `system.describe` and GET /health alike.
 */
export const APP_VERSION = '1.0.0';

/** Highest version present in both lists, or `null` when there is none. */
export function negotiateProtocolVersion(
  clientVersions: readonly number[],
  serverVersions: readonly number[] = SUPPORTED_PROTOCOL_VERSIONS,
): number | null {
  let best: number | null = null;
  for (const v of clientVersions) {
    if (serverVersions.includes(v) && (best === null || v > best)) best = v;
  }
  return best;
}

export function isSupportedProtocolVersion(v: unknown): v is number {
  return typeof v === 'number' && SUPPORTED_PROTOCOL_VERSIONS.includes(v);
}

/**
 * HTTP paths of the public API (the same on every server). `/api` and the versioned `/api/v<PROTOCOL_VERSION>` take
 * one JSON request envelope per POST; `/api/schema` (and `/api/v1/schema`) returns the machine-readable API
 * description (`system.describe`); `/ws` is the WebSocket endpoint; `/health` the liveness probe.
 */
export const API_PATHS = {
  api: '/api',
  versionedApi: `/api/v${PROTOCOL_VERSION}`,
  schema: '/api/schema',
  versionedSchema: `/api/v${PROTOCOL_VERSION}/schema`,
  ws: '/ws',
  health: '/health',
} as const;

/** A client/app version as numbers: `[major, minor, patch]`. */
export type VersionTriple = readonly [number, number, number];

/**
 * The version number in a client version string: the first `major.minor[.patch]` in it, so both `1.4.2` and
 * `fwp-web/1.4.2` (name/version) or `2.0.1-beta.3 (build 77)` parse. Null when there is none (e.g. `fwp-port`).
 */
export function parseVersion(text: string): VersionTriple | null {
  const m = /(\d{1,9})\.(\d{1,9})(?:\.(\d{1,9}))?/.exec(text);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3] ?? 0)] : null;
}

/** Negative, zero or positive like a comparator. */
export function compareVersions(a: VersionTriple, b: VersionTriple): number {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}

/**
 * True when a client must update before it may use this server: `minClientVersion` is set and the client's version
 * is lower, or the client sent no recognizable version number.
 */
export function isClientTooOld(clientVersion: string, minClientVersion: string | null): boolean {
  if (minClientVersion === null) return false;
  const min = parseVersion(minClientVersion);
  if (!min) return false;
  const v = parseVersion(clientVersion);
  return v === null || compareVersions(v, min) < 0;
}
